import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../services/global-environment';
import {
  MfaInitSetupRequest,
  MfaInitSetupResponse,
  MfaQrCodeRequest,
  MfaQrCodeResponse,
  MfaCompleteSetupRequest,
  MfaCompleteSetupResponse,
  MfaVerifyRequest,
  MfaVerifyResponse,
  MfaDisableRequest,
  MfaDisableResponse,
  MfaRegenerateBackupCodesRequest,
  MfaRegenerateBackupCodesResponse,
  MfaStatus,
  MfaSetupState
} from '../models/mfa.model';

@Injectable({
  providedIn: 'root'
})
export class MfaService {
  private apiUrl = environment.apiUrl;
  private mfaStatusSubject = new BehaviorSubject<MfaStatus>({ isEnabled: false, hasBackupCodes: false });
  private setupStateSubject = new BehaviorSubject<MfaSetupState>({ step: 'init' });

  public mfaStatus$ = this.mfaStatusSubject.asObservable();
  public setupState$ = this.setupStateSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Get authorization headers with JWT token
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Initialize MFA setup
   */
  initSetup(username: string): Observable<MfaInitSetupResponse> {
    const request: MfaInitSetupRequest = { username };

    return this.http.post<MfaInitSetupResponse>(
      `${this.apiUrl}/api/auth/mfa/init-setup`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Get QR Code for MFA setup
   */
  getQrCode(setupToken: string): Observable<MfaQrCodeResponse> {
    const params = new HttpParams().set('setupToken', setupToken);

    return this.http.get<MfaQrCodeResponse>(
      `${this.apiUrl}/api/auth/mfa/qrcode`,
      {
        params,
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Complete MFA setup with verification code
   */
  completeSetup(setupToken: string, verificationCode: string): Observable<MfaCompleteSetupResponse> {
    const request: MfaCompleteSetupRequest = { setupToken, verificationCode };

    return this.http.post<MfaCompleteSetupResponse>(
      `${this.apiUrl}/api/auth/mfa/complete-setup`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Verify MFA code during login
   */
  verify(username: string, verificationCode: string): Observable<MfaVerifyResponse> {
    const request: MfaVerifyRequest = { username, verificationCode };

    return this.http.post<MfaVerifyResponse>(
      `${this.apiUrl}/api/auth/mfa/verify`,
      request,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    );
  }

  /**
   * Disable MFA
   */
  disable(verificationCode: string, password: string): Observable<MfaDisableResponse> {
    const request: MfaDisableRequest = { verificationCode, password };

    return this.http.post<MfaDisableResponse>(
      `${this.apiUrl}/api/auth/mfa/disable`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Regenerate backup codes
   */
  regenerateBackupCodes(verificationCode: string): Observable<MfaRegenerateBackupCodesResponse> {
    const request: MfaRegenerateBackupCodesRequest = { verificationCode };

    return this.http.post<MfaRegenerateBackupCodesResponse>(
      `${this.apiUrl}/api/auth/mfa/regenerate-backup-codes`,
      request,
      {
        headers: this.getAuthHeaders()
      }
    );
  }

  /**
   * Update MFA status
   */
  updateMfaStatus(status: MfaStatus): void {
    this.mfaStatusSubject.next(status);
  }

  /**
   * Update setup state
   */
  updateSetupState(state: MfaSetupState): void {
    this.setupStateSubject.next(state);
  }

  /**
   * Get current MFA status
   */
  getCurrentStatus(): MfaStatus {
    return this.mfaStatusSubject.value;
  }

  /**
   * Get current setup state
   */
  getCurrentSetupState(): MfaSetupState {
    return this.setupStateSubject.value;
  }

  /**
   * Reset setup state
   */
  resetSetupState(): void {
    this.setupStateSubject.next({ step: 'init' });
  }

  /**
   * Check if user has MFA enabled (this would typically be called after login)
   */
  checkMfaStatus(username: string): Observable<MfaStatus> {
    // This endpoint would need to be implemented in the backend
    // For now, we'll return the current status
    return this.mfaStatus$;
  }
}
